import { Fragment } from 'react';

import { ApolloClient, gql } from '@apollo/client';
import {
  AlienIcon,
  GhostIcon,
  Separator,
  TTranslator,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';

import { TLocalesKeys } from 'localization';
import { EDuration, INormalizedTariff } from 'types';

import s from './TariffCard.module.scss';

const formatPrice = (price: number | '', currency: string): string => {
  if (price === '') {
    return '—';
  }

  return `${price.toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })} ${currency}`;
};

const formatDuration = (duration: EDuration): TLocalesKeys => {
  if (duration === EDuration.weeks) {
    return 'time.contracted.weeks';
  }

  if (duration === EDuration.days) {
    return 'time.contracted.days';
  }

  if (duration === EDuration.hours) {
    return 'time.contracted.hours';
  }

  return 'time.contracted.minutes';
};

const renderSchedule = (tariff: INormalizedTariff): JSX.Element | string => {
  if (!tariff.days) {
    return '—';
  }

  return (
    <div className={s['contracted-days']}>
      {tariff.days
        .map((day) => {
          const weekday = dayjs().day(day).format('dd');

          return weekday.charAt(0).toUpperCase() + weekday.slice(1);
        })
        .join(', ')}
      <span>
        {tariff.hours.start} — {tariff.hours.end}
      </span>
    </div>
  );
};

const renderGroups = (
  t: TTranslator<TLocalesKeys>,
  tariff: INormalizedTariff,
  currency: string,
  apolloClient: ApolloClient<unknown>,
  host_group?: number
): JSX.Element | Array<JSX.Element> | string => {
  if (host_group) {
    const priceListValue = tariff.price_list.find((item) =>
      item.host_groups?.some((group) => group === host_group)
    );

    if (!priceListValue) {
      return (
        <span className={s['group-placeholder']}>
          {t('tariff.priceList.placeholder')}
        </span>
      );
    }

    const groupIdentity = apolloClient.cache.identify({
      __typename: 'HostGroupOverview',
      id: host_group,
    });
    const groupData = apolloClient.readFragment<{ title: string }>({
      id: groupIdentity,
      fragment: gql`
        fragment GroupTitle on HostGroupOverview {
          title
        }
      `,
    });

    return (
      <div className={s.columns}>
        <div className={s['selected-group']}>
          <span className={s.gray}>{t('tariff.group')}</span>
          <div>{groupData?.title}</div>
        </div>
        <div className={s.prices}>
          <span className={cn(s.gray, s.prices_title)}>
            {t('tariff.price')}
          </span>
          <span title={t('tariff.prices.guest')}>
            <GhostIcon size="s" />
            {formatPrice(priceListValue.cost, currency)}
          </span>
          <span title={t('tariff.prices.clients')} className={s.gray}>
            <AlienIcon size="s" />
            {formatPrice(
              priceListValue.user_cost ?? priceListValue.cost,
              currency
            )}
          </span>
        </div>
      </div>
    );
  }

  return tariff.price_list.map((prices, index) => (
    <Fragment
      key={`tariff-${tariff.title}-${
        tariff.type
      }-prices-${prices.host_groups?.join('')}`}
    >
      {index !== 0 && <Separator />}
      <div className={s.columns}>
        <div className={s['groups']}>
          <span className={s.gray}>{t('tariff.groups')}</span>
          {prices.host_groups?.map((groupId) => {
            const groupIdentity = apolloClient.cache.identify({
              __typename: 'HostGroupOverview',
              id: groupId,
            });
            const groupData = apolloClient.readFragment<{ title: string }>({
              id: groupIdentity,
              fragment: gql`
                fragment GroupTitle on HostGroupOverview {
                  title
                }
              `,
            });

            return (
              <div
                className={s.group}
                key={`group-${groupId}-${groupData?.title}`}
              >
                {groupData?.title}
              </div>
            );
          })}
        </div>
        <div className={s.prices}>
          <span className={cn(s.gray, s.prices_title)}>
            {t('tariff.price')}
          </span>
          <span title={t('tariff.prices.guest')}>
            <GhostIcon size="s" />
            {formatPrice(prices.cost, currency)}
          </span>
          <span
            title={t('tariff.prices.clients')}
            className={cn(prices.user_cost === prices.cost && s.gray_color)}
          >
            <AlienIcon size="s" />
            {formatPrice(prices.user_cost ?? prices.cost, currency)}
          </span>
        </div>
      </div>
    </Fragment>
  ));
};

export { formatPrice, formatDuration, renderGroups, renderSchedule };
